// pages/EmailTemplate.tsx

import { Button, Flex, Spin } from "antd";
import DashboardLayout from "layout/DashboardLayout";
import PageTitle from "components/PageTitle";
import { useEffect, useState } from "react";
import { OrgSelector } from "components/Organisation/OrganisationSelector";
import { templateService, TemplateDto } from "api/template.service";
import { organisationService } from "api/organisation.service";
import { useSearchParams } from "react-router-dom";
import { storedTokens } from "utils/storedTokens";
import TemplateList from "components/Template/template-list";
import TemplateEditorPage from "components/Template/template-editor-page";
import { TbPlus, TbX } from "react-icons/tb";

type Mode = "list" | "editor";

const EmailTemplate = () => {
  const [sp, setSp] = useSearchParams();
  const variables = templateService.useGetVariables();

  const initialAction = sp.get("action") ?? undefined;
  const isCreatingNew = sp.get("new") === "true";
  const isEditing = Boolean(sp.get("id")?.length);
  const shouldOpenEditor = isCreatingNew || isEditing;

  const [mode, setMode] = useState<Mode>(() => (shouldOpenEditor ? "editor" : "list"));

  const organisations = organisationService.useGetAllOrganisations();
  const [selectedOrg, setSelectedOrg] = useState(() => {
    const queryOrgCode = sp.get("orgCode");
    return (queryOrgCode || storedTokens.orgCode.getToken()) ?? undefined;
  });
  const selectedOrgId = organisations.data?.find((o) => o.code === selectedOrg)?._id;

  const templates = templateService.useGetAllTemplates(selectedOrgId);
  const [templateId, setTemplateId] = useState(() => sp.get("id") ?? undefined);

  // useEffect(() => {
  //   if (!templates.isLoading) {
  //     const doesIdExist = templates.data?.result.data.some((d) => d._id === templateId) ?? false;
  //     console.log({ isLoading: templates.isLoading, doesIdExist });
  //     if (!doesIdExist) onUnselect();
  //   }
  // }, [templates.data?.result.data, templates.isLoading]);

  const onSelect = (template: TemplateDto) => {
    setTemplateId(template._id);
    setMode("editor");
    setSp((prev) => {
      prev.delete("new");
      prev.set("id", template._id);
      return prev;
    });
  };

  const onUnselect = () => {
    setTemplateId(undefined);
    setMode("list");
    setSp((prev) => {
      prev.delete("id");
      prev.delete("new");
      return prev;
    });
  };

  const onCreate = () => {
    setMode("editor");
    setSp((prev) => {
      prev.delete("id");
      prev.set("new", "true");
      return prev;
    });
  };

  return (
    <DashboardLayout
      headerRight={
        <Flex gap={8}>
          {mode === "editor" ? (
            <Button icon={<TbX />} onClick={onUnselect}>
              Cancel
            </Button>
          ) : (
            <Button type="primary" icon={<TbPlus />} onClick={onCreate}>
              New Template
            </Button>
          )}
          <OrgSelector
            organisations={organisations.data ?? []}
            selected={selectedOrg}
            onChange={(org) => {
              setSp((prev) => ({ ...prev, orgCode: org.code }));
              setSelectedOrg(org.code);
            }}
          />
        </Flex>
      }
    >
      <PageTitle title="Message Templates" pageTitle={[{ title: "Communications" }, { title: "Templates" }]} />

      {mode === "list" ? (
        <TemplateList
          templates={templates.data?.data ?? []}
          loading={templates.isLoading}
          orgCode={selectedOrg}
          onSelect={onSelect}
        />
      ) : (
        <>
          {variables.isLoading || templates.isLoading || !selectedOrgId ? (
            <Flex justify="center" style={{ padding: 48 }}>
              <Spin />
            </Flex>
          ) : (
            <TemplateEditorPage
              organisationId={selectedOrgId}
              orgCode={selectedOrg}
              template={templates.data?.data.find((d) => d._id === templateId) ?? null}
              variableMap={variables.data ?? {}}
              onBack={onUnselect}
              initialTriggers={initialAction ? [initialAction] : undefined}
            />
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default EmailTemplate;
